/* ==========================================================================
   RAM Bikes — Mapbox store locator ("Puntos de venta")
   ==========================================================================

   BEHAVIOUR
   Mapbox GL JS is ~230KB gzipped plus a stylesheet, which is more weight than
   the rest of this page combined. It is therefore NOT loaded on page load —
   the module waits until the map container is about to enter the viewport,
   then injects the CDN tags and initialises. On a visit that never scrolls to
   the locator, nothing is fetched at all.

   NO TOKEN? The section degrades to a styled address card with a link out to
   the platform map app. It never renders a broken or empty map.

   TOKEN HANDLING
   Resolution order:
     1. window.RAM_CONFIG.mapboxToken
     2. data-mapbox-token on the container element

   A Mapbox public token (pk.*) is designed to be visible in client code — it
   is not a secret in the way a server key is. It IS abusable if left
   unrestricted, so before launch scope it to your domains in the Mapbox
   dashboard (Account -> Tokens -> URL restrictions). Never put a secret
   token (sk.*) here.

   SHOPIFY
   On conversion this becomes a section setting, and the container renders as:
       data-mapbox-token="{{ section.settings.mapbox_token }}"
   See the schema stub in docs/shopify-conversion.md.
   ========================================================================== */

(function () {
  'use strict';

  var MAPBOX_VERSION = 'v3.6.0';
  var CDN_JS = 'https://api.mapbox.com/mapbox-gl-js/' + MAPBOX_VERSION + '/mapbox-gl.js';
  var CDN_CSS = 'https://api.mapbox.com/mapbox-gl-js/' + MAPBOX_VERSION + '/mapbox-gl.css';

  /* Store list. Coordinates are [lng, lat] — Mapbox order, not the lat/lng
     order most people expect.

     GEOCODED, not estimated. Mapbox Geocoding returns this address at
     rooftop accuracy. The previous pair was eyeballed off the street name and
     sat 175m north of the door — far enough to put the pin on the wrong side
     of Izazaga. If the address ever changes, re-run the geocoder rather than
     nudging the numbers by hand. */
  var STORES = [
    {
      name: 'RAM Bikes — Tienda oficial',
      address: 'José María Izazaga 65-Loc 12, Centro Histórico de la Cdad. de México, Centro, Cuauhtémoc, 06000 Ciudad de México, CDMX',
      phone: '+52 55 6499 4648',
      coords: [-99.138072, 19.426229],
      verified: true
    }
  ];

  var MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

  /* SHOPIFY: sections/store-locator.liquid renders its blocks as a JSON
     <script data-ram-stores> next to the container. When it exists and is
     well-formed it replaces the list above; otherwise the hardcoded store
     stays as the fallback. */
  function readStores(el) {
    var node = (el.parentNode && el.parentNode.querySelector('[data-ram-stores]')) ||
               document.querySelector('[data-ram-stores]');
    if (!node) return STORES;
    try {
      var list = JSON.parse(node.textContent);
      if (Array.isArray(list)) {
        list = list.filter(function (s) {
          return s && Array.isArray(s.coords) && isFinite(s.coords[0]) && isFinite(s.coords[1]) &&
                 !(s.coords[0] === 0 && s.coords[1] === 0);
        });
        if (list.length) return list;
      }
    } catch (e) { /* fall through */ }
    return STORES;
  }

  var loaderPromise = null;

  function loadMapbox() {
    if (loaderPromise) return loaderPromise;

    loaderPromise = new Promise(function (resolve, reject) {
      if (window.mapboxgl) return resolve(window.mapboxgl);

      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = CDN_CSS;
      document.head.appendChild(css);

      var script = document.createElement('script');
      script.src = CDN_JS;
      script.async = true;
      script.onload = function () {
        window.mapboxgl ? resolve(window.mapboxgl) : reject(new Error('mapbox-gl loaded but global missing'));
      };
      script.onerror = function () {
        reject(new Error('Failed to load Mapbox GL JS from CDN'));
      };
      document.head.appendChild(script);
    });

    return loaderPromise;
  }

  /* ---- search, distance, list (added on request) ------------------------ */

  function readStrings() {
    var node = document.querySelector('[data-ram-locator-strings]');
    try { return node ? JSON.parse(node.textContent) : {}; } catch (e) { return {}; }
  }
  var STR = readStrings();
  function fmt(key, vars) {
    var out = STR[key] || '';
    Object.keys(vars || {}).forEach(function (k) { out = out.split('%' + k + '%').join(vars[k]); });
    return out;
  }

  /* Great-circle distance in km between two [lng, lat] pairs. */
  function haversine(a, b) {
    var R = 6371, toRad = Math.PI / 180;
    var dLat = (b[1] - a[1]) * toRad, dLng = (b[0] - a[0]) * toRad;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(a[1] * toRad) * Math.cos(b[1] * toRad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(s));
  }
  function fmtKm(km) { return km < 10 ? km.toFixed(1) : Math.round(km); }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fullAddress(s) {
    return [s.address, s.city, s.state].filter(Boolean).join(', ');
  }

  /* Renders the card list. `origin` (optional [lng, lat]) adds distances and
     sorts nearest-first; `visible` (optional array) limits to a text match. */
  function renderList(el, stores, origin, visible) {
    var list = document.querySelector('[data-locator-list]');
    if (!list) return;
    var rows = stores.map(function (s, i) {
      return { store: s, index: i, km: origin ? haversine(origin, s.coords) : null };
    });
    if (visible) rows = rows.filter(function (r) { return visible.indexOf(r.store) !== -1; });
    if (origin) rows.sort(function (a, b) { return a.km - b.km; });

    list.innerHTML = rows.map(function (r, n) {
      var s = r.store;
      var nearest = origin && n === 0;
      var tel = s.phone ? '<p class="locator__phone"><a href="tel:' + escapeHtml(String(s.phone).replace(/\s/g, '')) + '">' + escapeHtml(s.phone) + '</a></p>' : '';
      return '' +
        '<li class="locator__card' + (nearest ? ' is-nearest' : '') + '" data-store-index="' + r.index + '">' +
          (s.type ? '<p class="locator__type">' + escapeHtml(s.type) + '</p>' : '') +
          '<h3 class="locator__name">' + escapeHtml(s.name) + '</h3>' +
          (r.km != null ? '<p class="locator__distance">' + (nearest ? escapeHtml(STR.nearest || '') + ' · ' : '') + fmt('km', { km: fmtKm(r.km) }) + '</p>' : '') +
          '<p class="locator__address">' + escapeHtml(fullAddress(s)) + '</p>' +
          (s.hours ? '<p class="locator__hours">' + escapeHtml(s.hours) + '</p>' : '') +
          tel +
          '<div class="locator__actions">' +
            '<a class="btn btn--outline btn--compact" href="' + mapsHref({ address: fullAddress(s) }) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(STR.directions || 'Cómo llegar') + '</a>' +
            (s.url ? '<a class="btn btn--outline btn--compact" href="' + escapeHtml(s.url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(String(s.url).replace(/^https?:\/\//, '').replace(/\/$/, '')) + '</a>' : '') +
          '</div>' +
        '</li>';
    }).join('');

    list.querySelectorAll('.locator__card').forEach(function (card) {
      card.addEventListener('click', function (event) {
        if (event.target.closest('a')) return;
        focusStore(el, Number(card.getAttribute('data-store-index')));
      });
    });
    return rows;
  }

  function setStatus(text) {
    var node = document.querySelector('[data-locator-status]');
    if (node) node.textContent = text || '';
  }

  /* Centre the map on one store and open its popup. */
  function focusStore(el, index) {
    var map = el._ramMap, markers = el._ramMarkers || [];
    var store = (el._ramStores || [])[index];
    if (!map || !store) return;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    map.flyTo({ center: store.coords, zoom: Math.max(map.getZoom(), 14), duration: reduceMotion ? 0 : 800 });
    markers.forEach(function (m, i) {
      m.getElement().classList.toggle('is-nearest', i === index);
      if (i === index && !m.getPopup().isOpen()) m.togglePopup();
      else if (i !== index && m.getPopup().isOpen()) m.togglePopup();
    });
  }

  /* Distances from `origin`, nearest first; map fits origin + nearest. */
  function showNearest(el, origin, placeName) {
    var stores = el._ramStores || STORES;
    var rows = renderList(el, stores, origin);
    if (!rows || !rows.length) return;
    var nearest = rows[0];
    setStatus(fmt('nearestTo', { place: placeName, name: nearest.store.name, km: fmtKm(nearest.km) }));

    var map = el._ramMap;
    if (map && window.mapboxgl) {
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var bounds = new mapboxgl.LngLatBounds(origin, origin).extend(nearest.store.coords);
      map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: reduceMotion ? 0 : 900 });
      if (el._ramOrigin) el._ramOrigin.remove();
      el._ramOrigin = new mapboxgl.Marker({ color: '#c8102e', scale: 0.8 }).setLngLat(origin).addTo(map);
      (el._ramMarkers || []).forEach(function (m, i) {
        m.getElement().classList.toggle('is-nearest', i === nearest.index);
      });
    }
  }

  /* Text fallback when there is no token: match city / state / address. */
  function filterByText(el, q) {
    var stores = el._ramStores || STORES;
    var needle = q.trim().toLowerCase();
    var hits = stores.filter(function (s) {
      return [s.city, s.state, s.postal, s.address, s.name].join(' ').toLowerCase().indexOf(needle) !== -1;
    });
    if (!hits.length) { renderList(el, stores); setStatus(fmt('notFound', { q: q })); return; }
    renderList(el, stores, null, hits);
    setStatus(fmt('filtered', { q: q, n: hits.length }));
    if (el._ramMap && hits.length) focusStore(el, stores.indexOf(hits[0]));
  }

  function geocode(query, token, country) {
    var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + encodeURIComponent(query) + '.json' +
      '?access_token=' + encodeURIComponent(token) +
      '&limit=1&language=es&types=place,postcode,locality,district,region,neighborhood,address' +
      (country ? '&country=' + encodeURIComponent(country) : '');
    return fetch(url).then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('geocoding ' + r.status)); })
      .then(function (json) {
        var f = json.features && json.features[0];
        return f ? { coords: f.center, name: f.text || f.place_name } : null;
      });
  }

  function bindSearch(el) {
    var form = document.querySelector('[data-locator-search]');
    if (!form || form._ramBound) return;
    form._ramBound = true;
    var input = form.querySelector('[data-locator-input]');
    var geoBtn = form.querySelector('[data-locator-geo]');
    var token = resolveToken(el);
    var country = el.getAttribute('data-country') || '';

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var q = (input.value || '').trim();
      if (!q) { renderList(el, el._ramStores || STORES); setStatus(''); return; }
      if (!token) { filterByText(el, q); return; }
      setStatus(STR.locating || '…');
      geocode(q, token, country).then(function (place) {
        if (!place) { filterByText(el, q); return; }
        showNearest(el, place.coords, place.name);
      }).catch(function () { filterByText(el, q); });
    });

    if (geoBtn && 'geolocation' in navigator) {
      geoBtn.hidden = false;
      geoBtn.addEventListener('click', function () {
        setStatus(STR.locating || '…');
        navigator.geolocation.getCurrentPosition(function (pos) {
          showNearest(el, [pos.coords.longitude, pos.coords.latitude], geoBtn.textContent.trim());
        }, function () {
          setStatus(STR.denied || '');
        }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
      });
    }
  }

  function resolveToken(el) {
    var cfg = window.RAM_CONFIG || {};
    var token = cfg.mapboxToken || el.getAttribute('data-mapbox-token') || '';
    token = token.trim();
    // Guard against an unreplaced Liquid placeholder shipping to production.
    if (!token || token.indexOf('{{') === 0 || token === 'REPLACE_ME') return '';
    return token;
  }

  function mapsHref(store) {
    var q = encodeURIComponent(store.address);
    // Apple Maps on Apple platforms, Google Maps everywhere else.
    var isApple = /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);
    return isApple ? 'https://maps.apple.com/?q=' + q : 'https://www.google.com/maps/search/?api=1&query=' + q;
  }

  function renderFallback(el, reason) {
    el.classList.add('is-fallback');
    el.removeAttribute('aria-busy');

    var stores = el._ramStores || STORES;
    var html = stores.map(function (s) {
      return '' +
        '<article class="locator__card">' +
          '<h3 class="locator__name">' + s.name + '</h3>' +
          '<p class="locator__address">' + s.address + '</p>' +
          (s.phone ? '<p class="locator__phone"><a href="tel:' + String(s.phone).replace(/\s/g, '') + '">' + s.phone + '</a></p>' : '') +
          '<a class="btn btn--outline btn--sm" href="' + mapsHref(s) + '" target="_blank" rel="noopener noreferrer">' +
            'Ver en mapa' +
          '</a>' +
        '</article>';
    }).join('');

    el.innerHTML = '<div class="locator__fallback">' + html + '</div>';

    if (reason) {
      // Visible to developers, invisible to shoppers.
      console.warn('[RAM map] Falling back to address card:', reason);
    }
  }

  function initMap(el, token) {
    return loadMapbox().then(function (mapboxgl) {
      mapboxgl.accessToken = token;

      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var stores = el._ramStores || STORES;
      var zoom = Number(el.getAttribute('data-zoom')) || 16;
      var style = el.getAttribute('data-style') || MAP_STYLE;

      var map = new mapboxgl.Map({
        container: el,
        style: style,
        center: stores[0].coords,
        /* 16 is measured, not chosen. The design's map (Figma 77:358) is a
           screenshot; its scale was recovered by matching a street pair
           across both renders — Simon Bolivar to 5 de Febrero spans 393px in
           the file and 385px in a Mapbox render at z16, a 2% difference. The
           previous 14 was four times too wide and showed half the city.

           The file's screenshot happens to sit the shop 157px left of centre.
           That is where whoever took it left the viewport, not a decision, so
           the map centres on the shop instead — which is also what a store
           locator should do. */
        zoom: zoom,
        cooperativeGestures: true, // ctrl/⌘ + scroll to zoom — stops the map hijacking page scroll
        attributionControl: true
      });

      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

      map.on('load', function () {
        el.removeAttribute('aria-busy');
        el.classList.add('is-ready');
      });

      map.on('error', function (e) {
        // A 401 here almost always means the token is missing scopes or the
        // URL restriction does not include this origin.
        console.error('[RAM map]', e && e.error ? e.error.message : e);
        renderFallback(el, 'Mapbox reported an error');
      });

      el._ramMarkers = stores.map(function (store) {
        var popup = new mapboxgl.Popup({ offset: 24, closeButton: true }).setHTML(
          '<strong>' + escapeHtml(store.name) + '</strong><br>' +
          escapeHtml(fullAddress(store)) + '<br>' +
          (store.phone ? '<a href="tel:' + escapeHtml(String(store.phone).replace(/\s/g, '')) + '">' + escapeHtml(store.phone) + '</a>' : '')
        );

        return new mapboxgl.Marker({ color: '#000000' })
          .setLngLat(store.coords)
          .setPopup(popup)
          .addTo(map);
      });

      if (stores.length > 1) {
        var bounds = new mapboxgl.LngLatBounds();
        stores.forEach(function (s) { bounds.extend(s.coords); });
        map.fitBounds(bounds, { padding: 64, duration: reduceMotion ? 0 : 800 });
      }

      // The root font-size drives layout; when it changes the container
      // resizes and Mapbox needs to be told.
      window.addEventListener('ram:scale', function () { map.resize(); });

      el._ramMap = map;
      return map;
    });
  }

  function setup(el) {
    el._ramStores = readStores(el);
    var token = resolveToken(el);

    renderList(el, el._ramStores);
    bindSearch(el);

    if (!token) {
      renderFallback(el, 'No Mapbox token configured');
      return;
    }

    el.setAttribute('aria-busy', 'true');

    initMap(el, token).catch(function (err) {
      renderFallback(el, err && err.message);
    });
  }

  function observe(el) {
    if (!('IntersectionObserver' in window)) {
      setup(el);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.disconnect();
          setup(entry.target);
        }
      });
    }, { rootMargin: '400px' }); // start loading just before it comes into view
    io.observe(el);
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-ram-map]');
    Array.prototype.forEach.call(nodes, observe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.RAMMap = { stores: STORES, boot: boot };
})();
